import { useState, useRef, useEffect } from "react";
import { isValidMedia } from "@/utils/isValidMedia";
import { uploadImage } from "@/lib/uploads";
import { generateThumbnail } from "@/lib/generateThumbnail";
import toast from "react-hot-toast";

interface UseMediaUploadProps {
  maxMediaCount?: number;
  userId?: string;
}

export enum Media {
  VIDEO = "video",
  IMAGE = "image",
}

export type UploadMedia = {
  id: string;
  type: Media;
  url: string;
};

interface UseMediaUploadReturn {
  media: File[];
  mediaPreview: string[];
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleMediaUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeMedia: (index: number) => void;
  uploadMedia: (
    onProgress?: (percent: number) => void
  ) => Promise<UploadMedia[]>;
  resetMedia: () => void;
  handleDroppedFiles: (files: File[]) => void;
  isVideoFile: boolean;
}

export const useMediaUpload = ({
  maxMediaCount = 4,
  userId,
}: UseMediaUploadProps = {}): UseMediaUploadReturn => {
  const [media, setMedia] = useState<File[]>([]);
  const [mediaPreview, setMediaPreview] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isVideoFile, setisVideoFile] = useState(false);

  // Clean up preview URLs when unmounting
  // Clean up preview URLs when unmounting
  useEffect(() => {
    return () => {
      mediaPreview.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [mediaPreview]);

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);

      const hasVideo = selectedFiles.some((file) =>
        file.type.startsWith("video/")
      );
      setisVideoFile(hasVideo);

      // Check if existing media already contains video
      const containsVideo = media.some((file) =>
        file.type.startsWith("video/")
      );
      const containsImage = media.some((file) =>
        file.type.startsWith("image/")
      );

      // If existing media has a video, do not allow any more additions
      if (containsVideo) {
        toast.error("Cannot add more media when a video is already added");
        return;
      }

      // Check if trying to add video when images already present
      const selectedHasVideo = selectedFiles.some((file) =>
        file.type.startsWith("video/")
      );
      if (containsImage && selectedHasVideo) {
        toast.error("Not allowed to add video when images are already added");
        return;
      }

      for (const file of selectedFiles) {
        const { valid, error } = isValidMedia(file);
        if (!valid) {
          toast.error(error || "Invalid media file");
          return;
        }
      }

      if (media.length + selectedFiles.length > maxMediaCount) {
        toast.error(`You can only upload up to ${maxMediaCount} media files`);
        return;
      }

      setMedia((prev) => [...prev, ...selectedFiles]);

      const newPreviews = selectedFiles.map((file) =>
        URL.createObjectURL(file)
      );
      setMediaPreview((prev) => [...prev, ...newPreviews]);
    }
  };

  const removeMedia = (index: number) => {
    const newMedia = [...media];
    newMedia.splice(index, 1);
    setMedia(newMedia);

    const newPreviews = [...mediaPreview];
    URL.revokeObjectURL(newPreviews[index]);
    newPreviews.splice(index, 1);
    setMediaPreview(newPreviews);
  };

  const uploadMedia = async (
    onProgress?: (percent: number) => void
  ): Promise<UploadMedia[]> => {
    if (!userId) {
      throw new Error("User ID is required for media upload");
    }

    const totalFiles = media.length;
    let completed = 0;

    const uploadedMedia = await Promise.all(
      media.map(async (file) => {
        if (!(file instanceof File)) return null;

        try {
          const progressCb = (fileProgress: number) => {
            const perFileWeight = 100 / totalFiles;
            const overallProgress = Math.min(
              100,
              Math.round(
                completed * perFileWeight + (fileProgress * perFileWeight) / 100
              )
            );
            if (onProgress) onProgress(overallProgress);
          };

          if (file.type.startsWith("image/")) {
            let files: File[] = [file];
            try {
              const thumbnailBlob = await generateThumbnail(file);
              const thumbnailFile = new File([thumbnailBlob], "thumbnail.jpg", {
                type: "image/jpeg",
              });
              files.push(thumbnailFile);
            } catch (err) {
              console.warn("Failed to generate thumbnail:", err);
            }

            const response = await uploadImage(files, userId, progressCb);
            completed++;
            return {
              id: response.media_id,
              type: Media.IMAGE,
              url: response.url,
            } satisfies UploadMedia;
          } else if (file.type.startsWith("video/")) {
            const response = await uploadImage([file], userId, progressCb);
            completed++;
            return {
              id: response.media_id,
              type: Media.VIDEO,
              url: response.url,
            } satisfies UploadMedia;
          }

          return null;
        } catch (error) {
          console.error("Upload failed:", error);
          return null;
        }
      })
    );

    return uploadedMedia.filter(
      (item): item is NonNullable<typeof item> => item !== null
    );
  };

  const resetMedia = () => {
    setMedia([]);
    setMediaPreview([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDroppedFiles = (files: File[]) => {
    const validFiles = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );

    validFiles.forEach((file) => {
      const previewURL = URL.createObjectURL(file);
      setMedia((prev) => [...prev, file]);
      setMediaPreview((prev) => [...prev, previewURL]);
    });
  };

  return {
    media,
    mediaPreview,
    fileInputRef,
    handleMediaUpload,
    removeMedia,
    uploadMedia,
    resetMedia,
    handleDroppedFiles,
    isVideoFile,
  };
};
